/**
 * Kickard Bot — Difficulty-parameterised, structure-aware strategy.
 *
 * Design goals (per spec, 2026-02-12):
 *   • Bot evaluates VISIBLE board state. Never reads opponent goalkeeper card.
 *   • Maintains its OWN structure: prefers refilling slots when board is weak.
 *   • Avoids exposing its own goalkeeper too early.
 *   • Avoids leaving only 1 Attack + 1 Defense unless score-justified.
 *   • Plays like a cautious strategic human, not a random attacker.
 *   • Profiles: Easy / Balanced (default) / Hard.
 *
 * Structure metric (for own side):
 *   structure = #attacks_filled + #defenses_filled + (gk ? 1 : 0)   // 0..6
 *   weak     = (gk is null) OR (structure <= profile.weakStructureThreshold)
 *
 * Decision order for a drawn non-joker card on phase 'drawn':
 *
 *   A. WEAK STRUCTURE branch:
 *      - If own GK empty AND we can place into GK → place GK (very high probability).
 *      - Otherwise prefer ANY placement (attacks first if ownAttacks<=1, else defenses)
 *        with probability profile.weakPlaceBias.
 *      - If we do attack, ONLY attack opp attack/defense lines (continuation).
 *        NEVER shoot at GK while own structure is weak unless behind on score AND
 *        drawn is strong (>= J) — guarded by profile.weakGkShotChance.
 *
 *   B. HEALTHY STRUCTURE branch:
 *      - Prefer continuation attacks (atk-line > def-line) over GK shots:
 *        defeating opp cards keeps the turn AND weakens opp.
 *      - GK shot only when no continuation attack exists AND
 *        (a) drawn is strong, OR
 *        (b) score-behind, OR
 *        (c) own GK is filled and a fresh card just rebuilt structure recently.
 *      - "Should I shoot or rebuild own GK?" mini-decision uses placeChance
 *        modulated by drawn strength + scoreDiff + profile.gkProtectionFactor.
 *
 * The bot maintains residual randomness (profile.randomness) so play stays
 * unpredictable and beatable.
 */

import type { Action, GameState, PlayerId, PlayerState, SlotType } from './types';
import {
  canAttackWithDrawn,
  countOccupied,
  hasAnyValidDoubleChanceAction,
  isJoker,
  validAttackTargets,
  validPlacements,
} from './rules';

export const BOT_DELAY_MS = 1500;
export const BOT_DECIDE_DELAY_MS = 1700;

export type Difficulty = 'easy' | 'balanced' | 'hard' | 'smart' | 'expert';

interface DifficultyProfile {
  /** % bias to PLACE (vs attack) when structure is weak. 0..1 */
  weakPlaceBias: number;
  /** % to shoot at GK while structure is weak. 0..1 (very low) */
  weakGkShotChance: number;
  /** Multiplier applied to base placeChance when own GK is empty. */
  gkProtectionFactor: number;
  /** Random noise added to each probability decision. */
  randomness: number;
  /** structure <= this → weak. (0..6) */
  weakStructureThreshold: number;
  /** Probability the bot takes a continuation attack (atk/def line) when valid. */
  continuationAttackChance: number;
}

const PROFILES: Record<Difficulty, DifficultyProfile> = {
  easy: {
    weakPlaceBias: 0.7,
    weakGkShotChance: 0.25,
    gkProtectionFactor: 1.25,
    randomness: 0.35,
    weakStructureThreshold: 4,
    continuationAttackChance: 0.78,
  },
  balanced: {
    weakPlaceBias: 0.72,
    weakGkShotChance: 0.2,
    gkProtectionFactor: 0.95,
    randomness: 0.18,
    weakStructureThreshold: 3,
    continuationAttackChance: 0.9,
  },
  hard: {
    weakPlaceBias: 0.85,
    weakGkShotChance: 0.1,
    gkProtectionFactor: 0.85,
    randomness: 0.06,
    weakStructureThreshold: 3,
    continuationAttackChance: 0.97,
  },
  // SMART profile (2026-02-13) — uses value-based action scoring instead of
  // probabilistic heuristic branches.
  smart: {
    weakPlaceBias: 0.9,
    weakGkShotChance: 0.05,
    gkProtectionFactor: 0.8,
    randomness: 0.04,
    weakStructureThreshold: 3,
    continuationAttackChance: 1.0,
  },
  // EXPERT profile (2026-02-13) — strongest non-cheating strategy. Default
  // for playtesting. Adds endgame urgency, GK-shot-first priority for strong
  // cards, and heavier waste penalties. See scoreActionExpert().
  expert: {
    weakPlaceBias: 0.95,
    weakGkShotChance: 0.04,
    gkProtectionFactor: 0.75,
    randomness: 0.02,
    weakStructureThreshold: 3,
    continuationAttackChance: 1.0,
  },
};

let _activeDifficulty: Difficulty = 'expert';

export function setBotDifficulty(d: Difficulty) {
  _activeDifficulty = d;
}
export function getBotDifficulty(): Difficulty {
  return _activeDifficulty;
}

function jitter(base: number, profile: DifficultyProfile): number {
  const delta = (Math.random() * 2 - 1) * profile.randomness;
  return Math.min(0.98, Math.max(0.02, base + delta));
}

function rankStrength(rank: string, value: number): number {
  if (rank === 'A') return 14;
  if (rank === '2') return 2;
  return value;
}

function ownStructure(me: PlayerState): number {
  return countOccupied(me.attacks) + countOccupied(me.defenses) + (me.goalkeeper ? 1 : 0);
}

function valueOfOppCard(
  state: GameState,
  t: { player: PlayerId; slotType: SlotType; slotIndex: number },
): number {
  // GK-blind invariant guard — the bot must never use opp.goalkeeper data.
  // This helper is only called for atk/def line targets in chooseBotAction;
  // returning 0 for GK is a defensive-coding belt-and-braces.
  if (t.slotType === 'goalkeeper') return 0;
  const opp = state.players[t.player];
  const c = t.slotType === 'attack' ? opp.attacks[t.slotIndex] : opp.defenses[t.slotIndex];
  if (!c) return 0;
  return c.rank === 'A' ? 20 : c.rank === '2' ? 1 : c.value;
}

// ============================================================================
//  SMART scorer (2026-02-13) — value-based action evaluation.
//
//  Scores every legal action (ATTACK target + PLACE slot) and returns the
//  highest-scoring one. Replaces the heuristic-branch path for the 'smart'
//  difficulty (now the default for playtesting).
//
//  Scoring philosophy:
//   • PATH-TO-GOAL has the highest payoff. Removing the last defense or last
//     attack to unlock the next line is worth a lot.
//   • STRONG CARDS (A/K/Q/J) are precious. Heavy penalty for wasting them on
//     weak low-impact attack targets (the exact bug reported: Q used to
//     remove a low atk card while own board needed structure).
//   • OWN STRUCTURE: empty GK / Defense / Attack slots boost placement scores
//     when the drawn card is strong. Weak cards prefer atk/def slots over GK.
//   • GK SHOT pays out high but only when path is reachable (validAttackTargets
//     guarantees that). Strong cards on open GK = near-guaranteed goal.
//   • Score deficit / lead nudges aggression / conservatism slightly.
//   • Bot is GK-blind: opp goalkeeper card value is never read; we use the
//     drawn card's strength as the only signal for "likely to score".
// ============================================================================

function cardValue(c: { rank: string; value: number } | null | undefined): number {
  if (!c) return 0;
  return c.rank === 'A' ? 14 : c.rank === '2' ? 2 : c.value;
}

type ScorableAction =
  | { type: 'ATTACK'; target: { player: PlayerId; slotType: SlotType; slotIndex: number } }
  | { type: 'PLACE'; slotType: SlotType; slotIndex: number };

function scoreAction(
  state: GameState,
  action: ScorableAction,
  drawn: { rank: string; value: number },
): number {
  const me = state.players[state.currentPlayer];
  const oppId: PlayerId = me.id === 'p1' ? 'p2' : 'p1';
  const opp = state.players[oppId];
  const drawnV = cardValue(drawn);
  const drawnIsStrong = drawn.rank === 'A' || drawnV >= 11; // A/K/Q/J
  const drawnIsAce = drawn.rank === 'A';
  const drawnIsVeryWeak = drawn.rank === '2' || drawnV <= 3;
  const ownAtks = countOccupied(me.attacks);
  const ownDefs = countOccupied(me.defenses);
  const ownGk = me.goalkeeper !== null;
  const ownStructureCount = ownAtks + ownDefs + (ownGk ? 1 : 0);
  const oppAtks = countOccupied(opp.attacks);
  const oppDefs = countOccupied(opp.defenses);
  const scoreDiff = me.score - opp.score;

  if (action.type === 'ATTACK') {
    const t = action.target;
    if (t.slotType === 'goalkeeper') {
      // Direct GK shot. Path was validated as reachable by validAttackTargets.
      // Reward expected scoring chance based on drawn strength only (GK-blind).
      let s = 0;
      if (drawnIsAce) s = 100;
      else if (drawn.rank === 'K') s = 88;
      else if (drawn.rank === 'Q') s = 75;
      else if (drawn.rank === 'J') s = 60;
      else if (drawnV >= 10) s = 48;
      else if (drawnV >= 8) s = 30;
      else if (drawnV >= 6) s = 14;
      else s = 4; // weak GK shot is still legal but very risky
      // Behind on score → more aggressive.
      if (scoreDiff <= -2) s += 12;
      else if (scoreDiff <= -1) s += 6;
      if (scoreDiff >= 2) s -= 8;
      return s;
    }
    if (t.slotType === 'attack') {
      const target = opp.attacks[t.slotIndex];
      const targetV = cardValue(target);
      let s = 18 + targetV * 1.3; // base removal value scales with target strength
      // Path-to-goal bonus: removing the LAST opp attack unlocks defense line.
      if (oppAtks === 1) s += 28;
      // High-card-waste penalty (the reported bug).
      if (drawnIsAce && targetV <= 8) s -= 50;
      else if (drawnIsStrong && targetV <= 5) s -= 38;
      else if (drawnIsStrong && targetV <= 7) s -= 22;
      // If we still have important empty own slots, penalise spending strong
      // card on an attack target further.
      if (drawnIsStrong && (!ownGk || ownStructureCount <= 2)) s -= 20;
      return s;
    }
    if (t.slotType === 'defense') {
      const target = opp.defenses[t.slotIndex];
      const targetV = cardValue(target);
      let s = 55 + targetV * 1.5; // defense removal opens path to GK
      if (oppDefs === 1) s += 35; // removing LAST defense → GK reachable next
      // Defense removal is the RIGHT use of strong cards — small bonus.
      if (drawnIsStrong) s += 8;
      // Avoid using extremely strong A on weak def when other defs exist.
      if (drawnIsAce && targetV <= 4 && oppDefs >= 2) s -= 12;
      return s;
    }
  }

  if (action.type === 'PLACE') {
    if (action.slotType === 'goalkeeper') {
      // Empty GK — strong cards belong here.
      let s = 0;
      if (drawn.rank === 'K') s = 92; // K is the canonical GK — highest priority
      else if (drawnIsAce) s = 70; // A is better used as attacker — still good for GK
      else if (drawn.rank === 'Q') s = 80;
      else if (drawn.rank === 'J') s = 64;
      else if (drawnV >= 10) s = 50;
      else if (drawnV >= 8) s = 32;
      else if (drawnV >= 6) s = 16;
      else s = -8; // very weak GK is fragile (rule 5)
      // Empty GK is dangerous: boost placement when own board is also weak.
      if (ownStructureCount <= 2) s += 12;
      // If own board structure is otherwise solid, GK placement is less urgent.
      if (ownAtks >= 2 && ownDefs >= 1) s -= 4;
      // Way ahead in score → reinforce defense.
      if (scoreDiff >= 2) s += 5;
      return s;
    }
    if (action.slotType === 'attack') {
      let s = 16;
      if (drawnIsAce) s += 35;
      else if (drawnIsStrong) s += 26;
      else if (drawnV >= 8) s += 14;
      else if (drawnV >= 5) s += 6;
      else s += 1;
      // Weak own structure → boost.
      if (ownStructureCount <= 2) s += 18;
      if (ownAtks === 0) s += 16; // critically need attackers
      else if (ownAtks === 1) s += 6;
      // Empty GK is more urgent than empty attack slot for strong cards.
      if (!ownGk && drawnIsStrong) s -= 18;
      return s;
    }
    if (action.slotType === 'defense') {
      let s = 18;
      if (drawn.rank === 'K') s += 26; // K is also great defender (but GK is better)
      else if (drawnIsStrong) s += 24;
      else if (drawnV >= 8) s += 14;
      else if (drawnV >= 5) s += 6;
      else s += 1;
      if (ownStructureCount <= 2) s += 16;
      if (ownDefs === 0) s += 12;
      // Empty GK is more urgent than empty defense slot for strong cards.
      if (!ownGk && drawnIsStrong) s -= 16;
      // Behind in score → slightly devalue defensive placement.
      if (scoreDiff <= -2) s -= 6;
      return s;
    }
  }

  return -Infinity;
}

/**
 * SMART decision: enumerate legal actions, score each, pick the best.
 * Adds tiny ±2 jitter only for tie-breaking. Returns the chosen ATTACK or
 * PLACE action. Caller still handles joker phases + turn-start separately.
 */
function chooseSmartDrawnAction(state: GameState): Action {
  const drawn = state.drawnCard!;
  const me = state.players[state.currentPlayer];
  const targets = validAttackTargets(state);
  const placements = validPlacements(me, drawn);

  const candidates: Array<{ action: ScorableAction; score: number }> = [];
  for (const t of targets) {
    const action: ScorableAction = { type: 'ATTACK', target: t };
    candidates.push({ action, score: scoreAction(state, action, drawn) + (Math.random() * 2 - 1) });
  }
  for (const p of placements) {
    const action: ScorableAction = { type: 'PLACE', slotType: p.slotType, slotIndex: p.slotIndex };
    candidates.push({ action, score: scoreAction(state, action, drawn) + (Math.random() * 2 - 1) });
  }

  if (candidates.length === 0) return { type: 'END_TURN' };

  candidates.sort((a, b) => b.score - a.score);
  const best = candidates[0].action;
  if (best.type === 'ATTACK') return { type: 'ATTACK', target: best.target };
  return { type: 'PLACE', slotType: best.slotType, slotIndex: best.slotIndex };
}

// ============================================================================
//  EXPERT scorer (2026-02-13) — strongest non-cheating strategy.
//
//  Extends SMART with:
//    • SHOOT-FIRST priority: strong cards (A/K/Q/J/10) on an open GK path
//      almost always shoot, even over premium placements like K-to-empty-GK.
//      Visible info only — GK value remains hidden (GK-blind).
//    • ENDGAME URGENCY: tracks `state.deck.length` and `state.half`. When the
//      deck is low (≤10 cards) and especially in the 2nd half, attack scores
//      get boosted and placement scores get penalized — don't waste strong
//      cards on long-term structure when the half is about to end.
//    • Heavier waste penalties on A/K/Q/J spent on weak field targets while
//      a goal path or last-blocker removal is available.
// ============================================================================

function endgameUrgency(state: GameState): number {
  // Returns 0..1 urgency factor. Sharper curve (2026-02-13 audit):
  //   • deck > 18           → 0       (no urgency, build freely)
  //   • deck 16..18         → 0..0.15 (light pressure)
  //   • deck 11..15         → 0.15..0.45 (moderate)
  //   • deck 6..10          → 0.45..0.85 (high)
  //   • deck ≤ 5            → 0.95..1.0 (MAXIMUM URGENCY — last few chances)
  //   • ×1.3 in 2nd half because no more cards are coming after this half.
  const deck = state.deck.length;
  const half = state.half ?? 1;
  let u: number;
  if (deck > 18) u = 0;
  else if (deck > 15) u = 0.15 * ((18 - deck) / 3);
  else if (deck > 10) u = 0.15 + 0.30 * ((15 - deck) / 5);
  else if (deck > 5) u = 0.45 + 0.40 * ((10 - deck) / 5);
  else u = 0.95 + 0.05 * Math.max(0, (5 - deck) / 5); // 0.95..1.0
  if (half >= 2) u = Math.min(1, u * 1.3);
  return Math.max(0, Math.min(1, u));
}

function scoreActionExpert(
  state: GameState,
  action: ScorableAction,
  drawn: { rank: string; value: number },
): number {
  // Start from the SMART score, then apply EXPERT overlays.
  let base = scoreAction(state, action, drawn);
  const urgency = endgameUrgency(state);
  const drawnV = cardValue(drawn);
  const drawnIsStrong = drawn.rank === 'A' || drawnV >= 11; // A/K/Q/J
  const drawnIsAce = drawn.rank === 'A';
  const drawnIsPremium = drawnIsStrong || drawnV >= 10; // A/K/Q/J/10

  if (action.type === 'ATTACK') {
    const t = action.target;
    if (t.slotType === 'goalkeeper') {
      // SHOOT-FIRST priority — strong cards on open GK path become the
      // dominant choice over any placement. Add a large premium so a K-shot
      // beats K-to-own-GK placement (SMART had the K-placement at 92 winning
      // out over GK shot at 88 — the exact bug reported).
      if (drawnIsAce) base += 60;
      else if (drawn.rank === 'K') base += 50;
      else if (drawn.rank === 'Q') base += 40;
      else if (drawn.rank === 'J') base += 30;
      else if (drawnV >= 10) base += 22;
      else if (drawnV >= 8) base += 14;
      else if (drawnV >= 6) base += 6;
      // Endgame urgency makes shooting even more attractive.
      base += urgency * 35;
      return base;
    }
    if (t.slotType === 'attack') {
      const target = state.players[t.player].attacks[t.slotIndex];
      const targetV = cardValue(target);
      const oppAtks = countOccupied(state.players[t.player].attacks);
      // EXTRA waste penalty for premium cards on weak atk targets.
      if (drawnIsPremium && targetV <= 6 && oppAtks > 1) base -= 20;
      if (drawnIsAce && targetV <= 8) base -= 20;
      // Endgame urgency: still useful if it's the last blocker; otherwise
      // de-emphasized (we want to shoot or remove defenses).
      if (oppAtks === 1) base += urgency * 20; // unlocks defense line
      else base -= urgency * 12;
      return base;
    }
    if (t.slotType === 'defense') {
      const opp = state.players[t.player];
      const oppDefs = countOccupied(opp.defenses);
      // Last defense removal is the most important non-shot action — bump in
      // endgame.
      if (oppDefs === 1) base += 25 + urgency * 25;
      else base += urgency * 10;
      return base;
    }
  }

  if (action.type === 'PLACE') {
    // ENDGAME: placements lose value progressively. At MAXIMUM URGENCY
    // (deck ≤ 5) strong cards almost never go into structure — the bot must
    // use them aggressively before the half ends.
    const maxUrgency = urgency >= 0.85;
    if (action.slotType === 'goalkeeper') {
      base -= urgency * 35;
      if (maxUrgency && drawnIsPremium) base -= 30;
    } else if (action.slotType === 'attack' || action.slotType === 'defense') {
      base -= urgency * 45;
      if (drawnIsPremium) base -= urgency * 30;
      if (maxUrgency && drawnIsPremium) base -= 35; // hard cliff at deck ≤ 5
    }
    return base;
  }

  return base;
}

function chooseExpertDrawnAction(state: GameState): Action {
  const drawn = state.drawnCard!;
  const me = state.players[state.currentPlayer];
  const targets = validAttackTargets(state);
  const placements = validPlacements(me, drawn);

  const candidates: Array<{ action: ScorableAction; score: number }> = [];
  for (const t of targets) {
    const action: ScorableAction = { type: 'ATTACK', target: t };
    // Tiny ±0.5 jitter only for tie-breaking — no real randomness.
    candidates.push({ action, score: scoreActionExpert(state, action, drawn) + (Math.random() - 0.5) });
  }
  for (const p of placements) {
    const action: ScorableAction = { type: 'PLACE', slotType: p.slotType, slotIndex: p.slotIndex };
    candidates.push({ action, score: scoreActionExpert(state, action, drawn) + (Math.random() - 0.5) });
  }

  if (candidates.length === 0) return { type: 'END_TURN' };

  candidates.sort((a, b) => b.score - a.score);
  const best = candidates[0].action;
  if (best.type === 'ATTACK') return { type: 'ATTACK', target: best.target };
  return { type: 'PLACE', slotType: best.slotType, slotIndex: best.slotIndex };
}

/**
 * Path-to-goal target selection (2026-02-13).
 *
 * The engine's progression rules (see canAttackWithDrawn):
 *   • Attack line  → always reachable
 *   • Defense line → only when oppAttacks ≤ 1
 *   • Goalkeeper   → only when oppAttacks ≤ 1 AND oppDefenses ≤ 1
 *
 * Therefore the strategic priority is "clear what's blocking the path to GK":
 *
 *   1. If oppAttacks ≥ 2 → defense is NOT reachable. The bot must clear the
 *      attack line first. Pick the strongest atk-line target.
 *
 *   2. If oppAttacks ≤ 1 AND defLineTargets exist → defense is reachable AND
 *      removing it opens the path to GK. Prefer DEFENSE. Strong drawn cards
 *      (A/K/Q/J) ALWAYS pick defense — never waste them on a low-impact atk
 *      card when a defender can be cleared.
 *
 *   3. Otherwise (no def-line target, e.g. opp has 0 defenses or only 1 atk
 *      that we can hit) → take the strongest atk-line target.
 *
 *   Residual randomness: even when defense is preferred, a small chance to
 *   grab a very strong atk-line card (>= 12 value) keeps play varied.
 */
function selectStrategicAttackTarget(
  state: GameState,
  atkLineTargets: Array<{ player: PlayerId; slotType: SlotType; slotIndex: number }>,
  defLineTargets: Array<{ player: PlayerId; slotType: SlotType; slotIndex: number }>,
  oppAttacks: number,
  drawnIsStrong: boolean,
): { player: PlayerId; slotType: SlotType; slotIndex: number } | null {
  const byValueDesc = (a: typeof atkLineTargets[number], b: typeof atkLineTargets[number]) =>
    valueOfOppCard(state, b) - valueOfOppCard(state, a);

  // Rule 1 — attack line not yet cleared (≥ 2 atks): defense unreachable.
  if (oppAttacks >= 2) {
    if (atkLineTargets.length > 0) return [...atkLineTargets].sort(byValueDesc)[0];
    return null;
  }

  // Defense is reachable (oppAttacks ≤ 1).
  if (defLineTargets.length > 0) {
    const bestDef = [...defLineTargets].sort(byValueDesc)[0];
    const bestAtk = atkLineTargets.length > 0 ? [...atkLineTargets].sort(byValueDesc)[0] : null;

    // Rule 3 — strong drawn card (A/K/Q/J) → defense, no question.
    if (drawnIsStrong) return bestDef;

    // Rule 6 — defense preferred to open path to goal. Small residual chance
    // to grab a very high-value attack card (>= 12) instead.
    if (bestAtk && valueOfOppCard(state, bestAtk) >= 12 && Math.random() < 0.2) {
      return bestAtk;
    }
    return bestDef;
  }

  // No defense reachable / no defense targets left → atk-line.
  if (atkLineTargets.length > 0) return [...atkLineTargets].sort(byValueDesc)[0];
  return null;
}

export function chooseBotAction(state: GameState): Action {
  if (state.phase === 'turn-start') return { type: 'DRAW' };

  // Joker V2 — bot decides SHOOT vs DOUBLE CHANCE, then resolves DC choice.
  if (state.phase === 'joker-choice') return chooseJokerChoice(state);
  if (state.phase === 'joker-double-action') return chooseJokerDoubleAction(state);

  if (state.phase !== 'drawn' || !state.drawnCard) return { type: 'END_TURN' };

  const profile = PROFILES[_activeDifficulty];
  const me = state.players[state.currentPlayer];
  const oppId: PlayerId = me.id === 'p1' ? 'p2' : 'p1';
  const opp = state.players[oppId];
  const drawn = state.drawnCard;

  if (isJoker(drawn)) return { type: 'END_TURN' };

  // EXPERT difficulty (2026-02-13 default) — strongest non-cheating strategy.
  // SHOOT-FIRST priority + endgame urgency + heavier waste penalties.
  if (_activeDifficulty === 'expert') {
    return chooseExpertDrawnAction(state);
  }
  // SMART difficulty — value-based scorer (no endgame urgency).
  if (_activeDifficulty === 'smart') {
    return chooseSmartDrawnAction(state);
  }

  const targets = validAttackTargets(state);
  const placements = validPlacements(me, drawn);

  const atkLineTargets = targets.filter((t) => t.slotType === 'attack');
  const defLineTargets = targets.filter((t) => t.slotType === 'defense');
  const gkTargets = targets.filter((t) => t.slotType === 'goalkeeper');

  const ownAttacks = countOccupied(me.attacks);
  const ownDefenses = countOccupied(me.defenses);
  const ownGkPresent = me.goalkeeper !== null;
  const structure = ownStructure(me);
  const scoreDiff = me.score - opp.score;
  const drawnValue = rankStrength(drawn.rank, drawn.value);
  const drawnIsStrong = drawn.rank === 'A' || drawnValue >= 11; // A, K, Q, J
  const drawnIsWeak = !drawnIsStrong && drawn.rank !== '2' && drawnValue <= 7;

  const weakStructure =
    !ownGkPresent || structure <= profile.weakStructureThreshold;

  // -------- A. WEAK STRUCTURE — heavily defensive --------
  if (weakStructure && placements.length > 0) {
    // 1. Own GK empty AND can place GK → strongly prefer placing GK but
    //    sometimes shoot when the drawn card is exceptional or score deficit
    //    demands it. Spec: "sometimes secure its own GK first if its own goal
    //    is also open and drawn card is strong" — i.e. NOT every time.
    //
    //    Rule 5 (2026-02-13): avoid placing VERY WEAK cards (2 or value ≤ 3)
    //    as goalkeeper if a better strategic use exists (other placement or
    //    attack target). A 2-of-GK is a one-shot fragile defense — almost
    //    always better to keep that card on the attack/defense line.
    const gkPlace = placements.find((p) => p.slotType === 'goalkeeper');
    const drawnIsVeryWeakForGk = drawn.rank === '2' || drawnValue <= 3;
    const hasBetterUse =
      placements.some((p) => p.slotType !== 'goalkeeper') ||
      atkLineTargets.length > 0 ||
      defLineTargets.length > 0;
    if (!ownGkPresent && gkPlace && !(drawnIsVeryWeakForGk && hasBetterUse)) {
      const gkShotAvail = gkTargets.length > 0;
      // Probability of shooting instead of placing.
      let shootInstead = 0;
      if (gkShotAvail) {
        if (drawn.rank === 'A') shootInstead = 0.45;
        else if (drawnIsStrong) shootInstead = 0.22;
        else if (drawnValue >= 9) shootInstead = 0.12;
        else shootInstead = 0.03;
        if (scoreDiff <= -2) shootInstead += 0.25;
        else if (scoreDiff <= -1) shootInstead += 0.12;
        if (scoreDiff >= 2) shootInstead -= 0.2;
        shootInstead = jitter(shootInstead, profile);
      }
      if (gkShotAvail && Math.random() < shootInstead) {
        return { type: 'ATTACK', target: gkTargets[0] };
      }
      return { type: 'PLACE', slotType: 'goalkeeper', slotIndex: 0 };
    }

    // 2. Prefer placement with high probability.
    const placeBias = jitter(profile.weakPlaceBias, profile);
    if (Math.random() < placeBias) {
      // Fill attacks first if attack count is very low.
      if (ownAttacks <= 1) {
        const atkPlace = placements.find((p) => p.slotType === 'attack');
        if (atkPlace) return { type: 'PLACE', slotType: 'attack', slotIndex: atkPlace.slotIndex };
      }
      // Then defenses to lock progression line.
      if (ownDefenses <= 1) {
        const defPlace = placements.find((p) => p.slotType === 'defense');
        if (defPlace) return { type: 'PLACE', slotType: 'defense', slotIndex: defPlace.slotIndex };
      }
      // Fall through to any placement.
      const atkPlace = placements.find((p) => p.slotType === 'attack');
      if (atkPlace) return { type: 'PLACE', slotType: 'attack', slotIndex: atkPlace.slotIndex };
      const defPlace = placements.find((p) => p.slotType === 'defense');
      if (defPlace) return { type: 'PLACE', slotType: 'defense', slotIndex: defPlace.slotIndex };
      if (gkPlace) return { type: 'PLACE', slotType: 'goalkeeper', slotIndex: 0 };
    }

    // 3. Weak structure but rolled to attack — only continuation attacks, never GK shot
    //    unless explicitly allowed by profile. Use path-to-goal targeting
    //    (2026-02-13): prefer DEFENSE when reachable and clearing it opens
    //    the path to GK; otherwise attack-line.
    {
      const strategic = selectStrategicAttackTarget(
        state,
        atkLineTargets,
        defLineTargets,
        countOccupied(opp.attacks),
        drawnIsStrong,
      );
      if (strategic) return { type: 'ATTACK', target: strategic };
    }
    // GK shot during weak structure — only under harsh conditions
    if (gkTargets.length > 0 && Math.random() < profile.weakGkShotChance) {
      return { type: 'ATTACK', target: gkTargets[0] };
    }
    // Otherwise fall through to default placement
    if (placements.length > 0) {
      const p = placements[0];
      return { type: 'PLACE', slotType: p.slotType, slotIndex: p.slotIndex };
    }
    if (gkTargets.length > 0) return { type: 'ATTACK', target: gkTargets[0] };
    return { type: 'END_TURN' };
  }

  // -------- B. HEALTHY STRUCTURE — standard balanced play --------

  // B1. Continuation attacks — use path-to-goal target selection (2026-02-13).
  //     When defense is reachable AND def-line targets exist, prefer DEFENSE
  //     to open the path to GK. Otherwise attack the strongest atk-line card.
  if ((atkLineTargets.length > 0 || defLineTargets.length > 0)
      && Math.random() < jitter(profile.continuationAttackChance, profile)) {
    const strategic = selectStrategicAttackTarget(
      state,
      atkLineTargets,
      defLineTargets,
      countOccupied(opp.attacks),
      drawnIsStrong,
    );
    if (strategic) return { type: 'ATTACK', target: strategic };
  }

  // B2. GK shot decision — strategic.
  if (gkTargets.length > 0) {
    const canPlaceOwnGk =
      !ownGkPresent && placements.some((p) => p.slotType === 'goalkeeper');

    if (canPlaceOwnGk) {
      // Determine defensive probability based on rank & score.
      let placeChance: number;
      if (drawn.rank === 'A') placeChance = 0.12;
      else if (drawn.rank === '2') placeChance = 0.7;
      else if (drawnValue <= 7) placeChance = 0.88;
      else if (drawnValue <= 10) placeChance = 0.6;
      else placeChance = 0.4; // J/Q/K

      // Score-state modulation
      if (scoreDiff >= 2) placeChance += 0.2;       // leading → conservative
      else if (scoreDiff >= 1) placeChance += 0.1;
      if (scoreDiff <= -2) placeChance -= 0.3;     // way behind → take risks
      else if (scoreDiff <= -1) placeChance -= 0.15;

      placeChance *= profile.gkProtectionFactor;
      placeChance = jitter(placeChance, profile);

      if (Math.random() < placeChance) {
        return { type: 'PLACE', slotType: 'goalkeeper', slotIndex: 0 };
      }
    }

    // Decide whether to shoot. With a healthy structure, shooting is fine for
    // strong cards or score-deficit; otherwise prefer placement when available.
    let shootBias: number;
    if (drawn.rank === 'A') shootBias = 0.95;
    else if (drawnIsStrong) shootBias = 0.78;
    else if (drawnValue >= 8) shootBias = 0.55;
    else shootBias = 0.3;
    if (scoreDiff <= -1) shootBias += 0.15;
    if (scoreDiff >= 1) shootBias -= 0.1;
    shootBias = jitter(shootBias, profile);

    if (placements.length > 0 && Math.random() > shootBias) {
      // Choose constructive placement instead.
      const atkPlace = placements.find((p) => p.slotType === 'attack');
      if (atkPlace) return { type: 'PLACE', slotType: 'attack', slotIndex: atkPlace.slotIndex };
      const defPlace = placements.find((p) => p.slotType === 'defense');
      if (defPlace) return { type: 'PLACE', slotType: 'defense', slotIndex: defPlace.slotIndex };
      const gkPlace = placements.find((p) => p.slotType === 'goalkeeper');
      if (gkPlace) return { type: 'PLACE', slotType: 'goalkeeper', slotIndex: 0 };
    }

    return { type: 'ATTACK', target: gkTargets[0] };
  }

  // B3. No attack available → PLACE constructively.
  if (placements.length > 0) {
    const gkPlace = placements.find((p) => p.slotType === 'goalkeeper');
    if (gkPlace && !ownGkPresent) {
      return { type: 'PLACE', slotType: 'goalkeeper', slotIndex: 0 };
    }
    // If structure is unbalanced, fix it.
    if (ownAttacks <= ownDefenses) {
      const atkPlace = placements.find((p) => p.slotType === 'attack');
      if (atkPlace) return { type: 'PLACE', slotType: 'attack', slotIndex: atkPlace.slotIndex };
    }
    const defPlace = placements.find((p) => p.slotType === 'defense');
    if (defPlace) return { type: 'PLACE', slotType: 'defense', slotIndex: defPlace.slotIndex };
    const atkPlace = placements.find((p) => p.slotType === 'attack');
    if (atkPlace) return { type: 'PLACE', slotType: 'attack', slotIndex: atkPlace.slotIndex };
    if (gkPlace) return { type: 'PLACE', slotType: 'goalkeeper', slotIndex: 0 };
    const first = placements[0];
    return { type: 'PLACE', slotType: first.slotType, slotIndex: first.slotIndex };
  }

  // SAFETY NET (2026-02-13 critical bug fix): NEVER voluntarily END_TURN while
  // a legal action exists. The probabilistic gates in B1/B2 can ALL fail when
  // the bot has a full structure (no placements) AND the opp blocks the GK
  // (oppAtks >= 2 → no gkTargets), leaving B1's `continuationAttackChance`
  // roll as the ONLY chance to attack. If that roll fails, the bot would
  // pass turn despite having legal attacks — a critical bug observed live.
  //
  // This guard fires AFTER all strategic branches have considered their
  // preferred plays and only when END_TURN was about to be returned.
  if (atkLineTargets.length > 0 || defLineTargets.length > 0) {
    const fallback = selectStrategicAttackTarget(
      state,
      atkLineTargets,
      defLineTargets,
      countOccupied(opp.attacks),
      drawnIsStrong,
    );
    if (fallback) return { type: 'ATTACK', target: fallback };
  }
  if (gkTargets.length > 0) {
    return { type: 'ATTACK', target: gkTargets[0] };
  }
  if (placements.length > 0) {
    const first = placements[0];
    return { type: 'PLACE', slotType: first.slotType, slotIndex: first.slotIndex };
  }

  return { type: 'END_TURN' };
}

/**
 * PHASE 4.3.1B — Simultaneous strategic penalty bot.
 *
 * Returns EXACTLY ONE penalty action, chosen based on which role the
 * bot currently plays:
 *   • Bot is SHOOTER  → returns `PENALTY_PICK_ATTACK` (own attack).
 *   • Bot is DEFENDER → returns `PENALTY_PICK_DEFENSE` (own GK).
 * Never dispatches `PENALTY_CONFIRM` — reveal is automatic in the
 * engine when both roles have locked.
 *
 * Called by the caller with `botPlayerId` explicitly bound so that
 * the choice-time state is INFORMATION-AVAILABLE-BEFORE-REVEAL for
 * the bot, exactly as it would be for a human opponent:
 *   • Bot's own hand + own `used` slots.
 *   • Opponent's `used` slots (public info — those cards are visible
 *     as face-down "used" markers in the UI).
 * The bot NEVER reads:
 *   • `pen.locks[opponentRole].index` — the opponent's current secret.
 *   • `pen.selection.attackIndex` / `.defenseIndex` for the opponent.
 *   • Opponent hand face values.
 *
 * Callers must pass the bot's player id explicitly. Passing the
 * shooter's id has always been safe (the pre-4.3.1B bot only ran as
 * shooter); the new defender path also works.
 */
export function chooseBotPenaltyAction(state: GameState, botPlayerId: PlayerId): Action | null {
  const pen = state.penalty;
  if (!pen) return null;
  if (pen.finished) return null;

  // Determine bot's current role from the AUTHORITATIVE currentShooter
  // — NOT from any lock/selection data (that could contain opponent
  // secret info in a mis-shared state; being defensive here).
  const isBotShooter = pen.currentShooter === botPlayerId;
  const otherId: PlayerId = botPlayerId === 'p1' ? 'p2' : 'p1';

  // Idempotency: if this role has already locked, do nothing.
  const locks = pen.locks;
  const myRoleLock = isBotShooter ? locks?.shooter : locks?.defender;
  if (myRoleLock?.locked) return null;

  if (isBotShooter) {
    // SHOOTER path — lead with strongest legal own attack.
    // Anti-cheat: only reads OWN hand + OWN used list.
    const myAttacks = pen.hands[botPlayerId].attacks;
    const usedA = pen.used[botPlayerId].attacks;
    let bestA = -1;
    let bestAVal = -1;
    myAttacks.forEach((c, i) => {
      if (usedA.includes(i)) return;
      const v = c.rank === 'A' ? 20 : c.rank === '2' ? 1 : c.value;
      if (v > bestAVal) { bestAVal = v; bestA = i; }
    });
    if (bestA < 0) return null;
    return { type: 'PENALTY_PICK_ATTACK', index: bestA };
  }

  // DEFENDER path — random legal own GK.
  // Anti-cheat: only reads OWN defense hand + OWN used defenses.
  // Deliberately random to mirror the previous "bot doesn't know
  // shooter's attack" fairness (no hidden info exploitation).
  const myDefenses = pen.hands[botPlayerId].defenses;
  const usedD = pen.used[botPlayerId].defenses;
  const availD: number[] = [];
  myDefenses.forEach((_c, i) => { if (!usedD.includes(i)) availD.push(i); });
  if (availD.length === 0) return null;
  const pickedD = availD[Math.floor(Math.random() * availD.length)];
  // Silence lint about `otherId` being unused — reserved for future
  // difficulty-aware strategies that read PUBLIC used-list info about
  // the opponent (never their hidden hand). Not used today.
  void otherId;
  return { type: 'PENALTY_PICK_DEFENSE', index: pickedD };
}

/**
 * Legacy bot-as-shooter combined action list. Retained for callers
 * that still expect the pre-4.3.1B "attack + defense + confirm"
 * shape. Delegates to `chooseBotPenaltyAction` for the shooter role
 * only; the defender role now belongs to the (potentially different)
 * defender caller.
 *
 * PHASE 4.3.1B — DEPRECATED. New callers must use
 * `chooseBotPenaltyAction(state, botPlayerId)` which respects the
 * simultaneous-lock model and secret-choice anti-cheat contract.
 */
export function chooseBotPenaltyActions(state: GameState): Action[] {
  const pen = state.penalty;
  if (!pen) return [];
  const shooter = pen.currentShooter;
  const shooterAction = chooseBotPenaltyAction(state, shooter);
  return shooterAction ? [shooterAction] : [];
}

// re-export so existing imports work
export { canAttackWithDrawn };

/**
 * Joker V2 — decide between SHOOT and DOUBLE CHANCE.
 *
 * Heuristic (GK-blind, structure-aware):
 *   • SHOOT if there is a strong immediate scoring opportunity:
 *       - Opponent's progression is met (oppAttacks≤1 AND oppDefenses≤1) so
 *         the joker-shot can reach the GK, AND
 *       - We are NOT in a defensive crisis (own structure ≥ profile threshold
 *         and own GK is present).
 *   • DOUBLE CHANCE if board control is more valuable:
 *       - Own GK is missing (DC placed on defense buys time), or
 *       - Defense line weak (≤ 1) and we can place there, or
 *       - Attack line weak (≤ 1) and we can place there, or
 *       - Opponent has strong cards in attack/defense we could remove with DC.
 *   • Residual randomness so the bot doesn't always pick the same option.
 */
function chooseJokerChoice(state: GameState): Action {
  const me = state.players[state.currentPlayer];
  const oppId: PlayerId = me.id === 'p1' ? 'p2' : 'p1';
  const opp = state.players[oppId];
  const profile = PROFILES[_activeDifficulty];

  // Soft-lock guard (2026-02-13): if no legal DC action exists, the engine
  // will fall back to SHOOT anyway — be explicit here so the bot's pacing
  // (e.g., decision delay buckets) reflects the real choice.
  if (!hasAnyValidDoubleChanceAction(state)) {
    return { type: 'JOKER_SHOOT' };
  }

  const ownAttacks = countOccupied(me.attacks);
  const ownDefenses = countOccupied(me.defenses);
  const ownGk = me.goalkeeper !== null;
  const structure = ownAttacks + ownDefenses + (ownGk ? 1 : 0);
  const oppAttacksCount = countOccupied(opp.attacks);
  const oppDefensesCount = countOccupied(opp.defenses);
  const oppOpenForShoot = oppAttacksCount <= 1 && oppDefensesCount <= 1;
  const scoreDiff = me.score - opp.score;

  // EXPERT joker decision (2026-02-13 audit) — true context-aware scoring.
  //
  // Computes a SHOOT score vs a DOUBLE CHANCE score across these factors:
  //   • oppOpenForShoot   — direct path to goal? Massive shoot bonus.
  //   • urgency           — late deck / 2nd half boosts SHOOT, depresses DC.
  //   • scoreDiff         — losing boosts SHOOT; comfortable lead boosts DC.
  //   • own structure     — weak board boosts DC (placement value).
  //   • opp board pressure — many opp atks/defs make a DC card more useful.
  //
  // Picks whichever scores higher. Symmetric: no random tie-break (DC ties
  // resolve to SHOOT since SHOOT is the canonical "use the joker now").
  if (_activeDifficulty === 'expert') {
    const urgency = endgameUrgency(state);
    const oppBoardPressure = oppAttacksCount + oppDefensesCount; // 0..5

    // SHOOT scoring.
    let shootScore = 0;
    if (oppOpenForShoot) shootScore += 100; // wide-open goal — near-guaranteed
    else shootScore += 25; // shoot through GK guard (1 atk OR 1 def still in the way) — still legal but harder
    shootScore += urgency * 70;
    if (scoreDiff <= -2) shootScore += 30; // losing badly → need goals
    else if (scoreDiff <= -1) shootScore += 15;
    if (scoreDiff >= 2) shootScore -= 15; // comfortable lead → control matters more
    if (state.half === 2 && urgency >= 0.5) shootScore += 25; // late 2nd half = critical

    // DOUBLE CHANCE scoring.
    let dcScore = 0;
    if (!oppOpenForShoot) dcScore += 50; // DC most useful when path blocked
    if (oppBoardPressure >= 3) dcScore += 18; // many targets to attack with DC
    if (structure <= 2) dcScore += 22; // weak own board → DC placement value
    if (!ownGk) dcScore += 14; // DC GK is a strong hidden defender
    if (scoreDiff >= 2) dcScore += 16; // protect a lead with board control
    if (scoreDiff <= -2) dcScore -= 12; // losing badly → DC delays scoring
    dcScore -= urgency * 70; // endgame kills DC's long-term value
    if (urgency >= 0.85) dcScore -= 50; // hard cliff at deck ≤ 5

    if (shootScore >= dcScore) return { type: 'JOKER_SHOOT' };
    return { type: 'JOKER_DOUBLE_CHOOSE' };
  }

  // Base lean toward SHOOT when goal is reachable.
  let shootBias = oppOpenForShoot ? 0.65 : 0.18;

  // Defensive considerations push toward DOUBLE CHANCE.
  if (!ownGk) shootBias -= 0.35;
  if (structure <= profile.weakStructureThreshold) shootBias -= 0.2;
  if (ownAttacks <= 1) shootBias -= 0.1;
  if (ownDefenses === 0) shootBias -= 0.12;

  // Score-state modulation.
  if (scoreDiff <= -2) shootBias += 0.15;
  if (scoreDiff >= 2) shootBias -= 0.1;

  shootBias = jitter(shootBias, profile);
  if (Math.random() < shootBias) return { type: 'JOKER_SHOOT' };
  return { type: 'JOKER_DOUBLE_CHOOSE' };
}

/**
 * Joker V2 — having chosen DOUBLE CHANCE, the bot now picks ATTACK or PLACE.
 *
 *   • If own structure is weak (especially missing GK or defenses) → PLACE the
 *     joker into the weakest slot (defense first if def<=1, else attack).
 *   • If opponent has strong cards reachable → ATTACK the strongest opp atk/def
 *     card to swing the board.
 *   • Otherwise lean PLACE (DC on defense is a heavy block).
 */
function chooseJokerDoubleAction(state: GameState): Action {
  const me = state.players[state.currentPlayer];
  const oppId: PlayerId = me.id === 'p1' ? 'p2' : 'p1';
  const opp = state.players[oppId];

  const ownAttacks = countOccupied(me.attacks);
  const ownDefenses = countOccupied(me.defenses);

  // Find an opp attack/def target — prefer the strongest reachable card.
  const candidates: Array<{ player: PlayerId; slotType: 'attack' | 'defense'; slotIndex: number; value: number }> = [];
  opp.attacks.forEach((c, i) => {
    if (!c) return;
    const v = c.rank === 'A' ? 20 : c.rank === '2' ? 1 : c.value;
    candidates.push({ player: opp.id, slotType: 'attack', slotIndex: i, value: v });
  });
  opp.defenses.forEach((c, i) => {
    if (!c) return;
    const v = c.rank === 'A' ? 20 : c.rank === '2' ? 1 : c.value;
    candidates.push({ player: opp.id, slotType: 'defense', slotIndex: i, value: v });
  });
  candidates.sort((a, b) => b.value - a.value);
  const strongestOpp = candidates[0];

  // Find an empty own slot. Priority:
  //   1. GK if missing (huge defensive value as hidden DC GK).
  //   2. Defense if weak.
  //   3. Attack.
  let placeTarget: { slotType: SlotType; slotIndex: number } | null = null;
  if (me.goalkeeper === null) {
    placeTarget = { slotType: 'goalkeeper', slotIndex: 0 };
  }
  if (!placeTarget && ownDefenses <= 1) {
    const i = me.defenses.findIndex((c) => c === null);
    if (i >= 0) placeTarget = { slotType: 'defense', slotIndex: i };
  }
  if (!placeTarget && ownAttacks <= 2) {
    const i = me.attacks.findIndex((c) => c === null);
    if (i >= 0) placeTarget = { slotType: 'attack', slotIndex: i };
  }
  if (!placeTarget) {
    const i = me.attacks.findIndex((c) => c === null);
    if (i >= 0) placeTarget = { slotType: 'attack', slotIndex: i };
    else {
      const j = me.defenses.findIndex((c) => c === null);
      if (j >= 0) placeTarget = { slotType: 'defense', slotIndex: j };
    }
  }

  // Heuristic decision.
  // - If there's a strong opp card (>=10) AND we have decent structure → ATTACK.
  // - Otherwise if a placement is available → PLACE.
  // - Fallback: attack anything; if no target, attack the strongest opp card.
  if (strongestOpp && strongestOpp.value >= 10 && Math.random() < 0.6) {
    return { type: 'JOKER_DOUBLE_ATTACK', target: { player: strongestOpp.player, slotType: strongestOpp.slotType, slotIndex: strongestOpp.slotIndex } };
  }
  if (placeTarget) {
    return { type: 'JOKER_DOUBLE_PLACE', slotType: placeTarget.slotType, slotIndex: placeTarget.slotIndex };
  }
  if (strongestOpp) {
    return { type: 'JOKER_DOUBLE_ATTACK', target: { player: strongestOpp.player, slotType: strongestOpp.slotType, slotIndex: strongestOpp.slotIndex } };
  }
  // Truly nothing — fallback to END_TURN (discards joker).
  return { type: 'END_TURN' };
}
